import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { DataTable } from '../components/DataTable.tsx';
import { ItemLabel } from '../components/ItemLabel.tsx';
import { Badge, Card, Empty, Notice, Spinner, StatTile } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { batchPrice, money, number, percent, relativeTime, unitMoney } from '../lib/format.ts';
import { ErrorState } from './Upgrade.tsx';

/**
 * Every shop selling one item.
 *
 * Search results cap at eight offers each - right when scanning several items,
 * useless once you have decided what you want. This is the whole list, sortable
 * and searchable, and it has its own URL so it can be shared or bookmarked.
 */
export function ItemPage() {
  const { itemKey = '' } = useParams();
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const item = useAsync(() => api.market.item(itemKey, includeEmpty), [itemKey, includeEmpty]);

  if (item.loading) return <Spinner />;
  if (item.error) return <ErrorState error={item.error} />;
  const data = item.data;
  if (!data) return <Empty title={`Nobody lists "${itemKey}"`} />;

  const band = data.band;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>
            <ItemLabel itemKey={data.itemKey} material={data.material} size={28} />
          </h1>
          <p>
            Every shop selling this, cheapest first.{' '}
            <Link to="/where-to-buy">Back to search</Link>
          </p>
        </div>
        <label className="row small" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={includeEmpty}
            onChange={(event) => setIncludeEmpty(event.target.checked)}
          />
          Show empty shops
        </label>
      </header>

      <div className="grid tiles">
        <StatTile
          label="Shops in stock"
          value={number(data.totals.shops)}
          foot={
            data.suppressed > 0 ? (
              <span className="muted">{number(data.suppressed)} decoys hidden</span>
            ) : undefined
          }
        />
        <StatTile label="Units available" value={number(data.totals.units)} />
        <StatTile
          label="Cheapest"
          value={data.totals.cheapest == null ? '-' : unitMoney(data.totals.cheapest)}
        />
        <StatTile
          label="Suggested"
          value={band ? unitMoney(band.recommended) : '-'}
          foot={band ? <span className="muted">{basisOf(band)}</span> : undefined}
        />
      </div>

      {band && (
        <Card title="What it is worth" subtitle={band.recommendedReason}>
          <div className="price-band" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
            <span className="head">Low</span>
            <span className="head">Average</span>
            <span className="head">High</span>
            <span className="head rec">Suggested</span>
            <span>{unitMoney(band.low)}</span>
            <span>{unitMoney(band.average)}</span>
            <span>{unitMoney(band.high)}</span>
            <span className="rec">{unitMoney(band.recommended)}</span>
          </div>
        </Card>
      )}

      <Card title={`${number(data.offers.length)} shop${data.offers.length === 1 ? '' : 's'}`}>
        {data.offers.length === 0 ? (
          <Empty title="Nothing in stock right now">
            Empty chests are hidden by default - tick the box above to see them.
          </Empty>
        ) : (
          <DataTable
            rows={data.offers}
            rowKey={(row) => String(row.shopId)}
            searchPlaceholder="Search owner, world or coordinates"
            pageSize={50}
            columns={buildColumns(band)}
          />
        )}
      </Card>

      {data.suppressed > 0 && (
        <Notice>
          {number(data.suppressed)} listing{data.suppressed === 1 ? '' : 's'} hidden as decoys -
          duplicate signs from one owner never seen selling. They would otherwise send you to an
          empty chest.
        </Notice>
      )}
    </>
  );
}

type Band = NonNullable<Awaited<ReturnType<typeof api.market.item>>['band']>;
type Offer = Awaited<ReturnType<typeof api.market.item>>['offers'][number];

/** Which evidence the suggestion rests on, in descending order of trust. */
function basisOf(band: Band): string {
  if (band.tradeCount > 0) return `${number(band.tradeCount)} recorded trades`;
  if (band.clearingUnits > 0) return `${number(band.clearingUnits)} units watched`;
  return 'from asking prices only';
}

function buildColumns(band: Band | null) {
  return [
    {
      key: 'owner',
      header: 'Shop',
      searchText: (row: Offer) => `${row.ownerName ?? ''} ${row.world ?? ''} ${row.coords ?? ''}`,
      sortValue: (row: Offer) => row.ownerName,
      render: (row: Offer) => (
        <span>
          <strong>{row.ownerName ?? 'unknown'}</strong>
          {row.adminShop && <Badge tone="muted">admin</Badge>}
        </span>
      ),
    },
    {
      key: 'sign',
      header: 'Sign',
      align: 'num' as const,
      sortValue: (row: Offer) => row.unitPrice,
      render: (row: Offer) => batchPrice(row.signPrice, row.batchQty) ?? '-',
    },
    {
      key: 'unit',
      header: 'Per unit',
      align: 'num' as const,
      sortValue: (row: Offer) => row.unitPrice,
      render: (row: Offer) => <strong>{unitMoney(row.unitPrice)}</strong>,
    },
    {
      key: 'vs',
      header: 'vs suggested',
      align: 'num' as const,
      sortValue: (row: Offer) =>
        band && band.recommended > 0
          ? ((row.unitPrice - band.recommended) / band.recommended) * 100
          : null,
      render: (row: Offer) => {
        if (!band || band.recommended <= 0) return <span className="muted">-</span>;
        const delta = ((row.unitPrice - band.recommended) / band.recommended) * 100;
        return (
          <Badge tone={delta > 10 ? 'critical' : delta < -10 ? 'good' : 'muted'}>
            {percent(delta)}
          </Badge>
        );
      },
    },
    {
      key: 'stock',
      header: 'Stock',
      align: 'num' as const,
      sortValue: (row: Offer) => row.currentStock,
      render: (row: Offer) =>
        (row.currentStock ?? 0) === 0 ? (
          <Badge tone="warning">empty</Badge>
        ) : (
          number(row.currentStock ?? 0)
        ),
    },
    {
      key: 'value',
      header: 'Stock value',
      align: 'num' as const,
      sortValue: (row: Offer) => (row.currentStock ?? 0) * row.unitPrice,
      render: (row: Offer) => money((row.currentStock ?? 0) * row.unitPrice),
    },
    {
      key: 'where',
      header: 'Location',
      sortValue: (row: Offer) => row.world,
      render: (row: Offer) => (
        <span className="muted small">
          {row.world ?? '-'}
          {row.coords ? <div>{row.coords}</div> : null}
          {row.stockAt ? <div>read {relativeTime(row.stockAt)}</div> : null}
        </span>
      ),
    },
  ];
}
